import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { AlignCenter, AlignLeft, AlignRight, Bold, Italic, PenLine } from 'lucide-react';

import { ToolBtn } from '../../shared/ui/tool-btn';
import { FONT_FAMILY_OPTIONS } from '../../text/constants/fonts';
import { FONT_UI } from '../../constants/design-tokens';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useTextHighlights } from '../../text/hooks/use-text-highlights';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer, useSelectedLayerId } from '../../selection/hooks/use-selected-layer';
import { TextContent } from '../../model/types';
import { AutoFitTextAlign, AutoFitTextbox } from '../types';
import { AF_BOLD_WEIGHT, AF_REGULAR_WEIGHT } from '../constants/defaults';
import {
  getAutoFitTextResolvedFontSize,
  setAutoFitTextAlign,
  setAutoFitTextCharSpacing,
  setAutoFitTextFill,
  setAutoFitTextFontFamily,
  setAutoFitTextFontStyle,
  setAutoFitTextFontWeight,
  setAutoFitTextLineHeight
} from '../utils/auto-fit-text-accessors';
import { labelStyle, rowStyle, sectionStyle, sliderStyle, valueLabelStyle } from './auto-fit-text-styles';
import { Fill, toFabricFill } from '../../shared/utils/fill';
import { FillControl } from '../../shared/ui/fill-control';

const DEFAULT_FILL = '#000000';

type Props = { textbox: AutoFitTextbox };

export const AutoFitTextTypographyRow: React.FC<Props> = ({ textbox }) => {
  const { canvas } = useEditorCanvas();
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const layer = useSelectedLayer();

  const [fontFamily, setFontFamily] = useState('DM Sans');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [textAlign, setTextAlign] = useState<AutoFitTextAlign>('center');
  const [resolvedSize, setResolvedSize] = useState(getAutoFitTextResolvedFontSize(textbox));
  const [lineHeight, setLineHeight] = useState(1.16);
  const [charSpacing, setCharSpacing] = useState(0);
  const [fill, setFillState] = useState<Fill>({ kind: 'solid', color: DEFAULT_FILL });

  const highlights = useTextHighlights(textbox, canvas);

  // Sync the controls from the model. resolvedSize is a derived render value (auto-fit output).
  useEffect(() => {
    if (layer?.kind !== 'text') return;
    const c = layer.content;
    setFontFamily(c.fontFamily);
    setIsBold(Number(c.fontWeight) >= 600);
    setIsItalic(c.fontStyle === 'italic');
    setTextAlign((c.textAlign as AutoFitTextAlign) ?? 'center');
    setLineHeight(c.lineHeight ?? 1.16);
    setCharSpacing(c.charSpacing ?? 0);
    setFillState(c.fill);
    setResolvedSize(getAutoFitTextResolvedFontSize(textbox));
  }, [layer, textbox]);

  const patch = (p: Partial<TextContent>) => {
    if (layerId) dispatch({ type: 'patchLayerContent', id: layerId, patch: p });
  };

  const onFontFamily = (v: string) => {
    setFontFamily(v);
    setAutoFitTextFontFamily(textbox, v);
    setResolvedSize(getAutoFitTextResolvedFontSize(textbox));
    patch({ fontFamily: v });
  };

  const toggleBold = () => {
    const next = !isBold;
    setIsBold(next);
    const weight = next ? AF_BOLD_WEIGHT : AF_REGULAR_WEIGHT;
    setAutoFitTextFontWeight(textbox, weight);
    setResolvedSize(getAutoFitTextResolvedFontSize(textbox));
    patch({ fontWeight: weight });
  };

  const toggleItalic = () => {
    const next = !isItalic;
    setIsItalic(next);
    const style = next ? 'italic' : 'normal';
    setAutoFitTextFontStyle(textbox, style);
    patch({ fontStyle: style });
  };

  const onAlign = (v: AutoFitTextAlign) => {
    setTextAlign(v);
    setAutoFitTextAlign(textbox, v);
    patch({ textAlign: v });
  };

  // Sliders: the accessor mutation is the live preview; the commit records to the model.
  const onLineHeight = (v: number) => {
    setLineHeight(v);
    setAutoFitTextLineHeight(textbox, v);
    setResolvedSize(getAutoFitTextResolvedFontSize(textbox));
  };
  const commitLineHeight = () => patch({ lineHeight });

  const onCharSpacing = (v: number) => {
    setCharSpacing(v);
    setAutoFitTextCharSpacing(textbox, v);
    setResolvedSize(getAutoFitTextResolvedFontSize(textbox));
  };
  const commitCharSpacing = () => patch({ charSpacing });

  const preview = (v: Fill) => {
    setFillState(v);
    const fabricFill = toFabricFill(v);
    setAutoFitTextFill(textbox, typeof fabricFill === 'string' ? fabricFill : DEFAULT_FILL);
  };

  const commit = (v: Fill) => {
    preview(v);
    patch({ fill: v });
  };

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Tipografía</div>

      <div style={{ ...rowStyle, marginBottom: 8 }}>
        <Select
          value={fontFamily}
          onChange={onFontFamily}
          options={FONT_FAMILY_OPTIONS}
          size='small'
          style={{ flex: 1, fontFamily: FONT_UI }}
        />
        <span style={valueLabelStyle}>{Math.round(resolvedSize)}px</span>
      </div>

      <div style={rowStyle}>
        <FillControl value={fill} onPreview={preview} onCommit={commit} gradient={false} />
        <ToolBtn onClick={toggleBold} title='Negrita' active={isBold}>
          <Bold size={15} />
        </ToolBtn>
        <ToolBtn onClick={toggleItalic} title='Itálica' active={isItalic}>
          <Italic size={15} />
        </ToolBtn>
        <ToolBtn
          onClick={highlights.toggleTight}
          title='Resaltar ajustado a la letra (requiere selección)'
          active={highlights.tightActive}
        >
          <PenLine size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('left')} title='Alinear izquierda' active={textAlign === 'left'}>
          <AlignLeft size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('center')} title='Centrar' active={textAlign === 'center'}>
          <AlignCenter size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('right')} title='Alinear derecha' active={textAlign === 'right'}>
          <AlignRight size={15} />
        </ToolBtn>
      </div>

      <div style={{ ...rowStyle, marginTop: 8 }}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Interlineado</span>
        <input
          type='range'
          min={0.8}
          max={3}
          step={0.05}
          value={lineHeight}
          onChange={e => onLineHeight(Number(e.target.value))}
          onPointerUp={commitLineHeight}
          style={sliderStyle}
        />
        <span style={valueLabelStyle}>{lineHeight.toFixed(2)}</span>
      </div>

      <div style={{ ...rowStyle, marginTop: 6 }}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Tracking</span>
        <input
          type='range'
          min={-100}
          max={500}
          step={10}
          value={charSpacing}
          onChange={e => onCharSpacing(Number(e.target.value))}
          onPointerUp={commitCharSpacing}
          style={sliderStyle}
        />
        <span style={valueLabelStyle}>{charSpacing}</span>
      </div>
    </div>
  );
};
